import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { validateData } from '../utils/runtimeValidation.ts';

interface DemographicsFormProps {
  onSubmit: (data: Record<string, unknown>) => void;
  initialData?: Record<string, unknown>;
}

declare global {
  interface Window {
    _e2eTestRunId?: string;
  }
}

const getE2eTestRunId = () => {
  if (typeof window !== 'undefined' && window._e2eTestRunId) {
    return window._e2eTestRunId;
  }
  return '';
};

const defaultFormData = {
  age: '',
  gender: '',
  education: '',
  city_size: '',
  psychiatric_diagnoses: '',
  other: '',
  e2eTestRunId: getE2eTestRunId() || ''
};

/**
 * @typedef {Object} Demographics
 * @property {string} [e2eTestRunId]
 * @property {string} [age]
 * @property {string} [gender]
 * @property {string} [education]
 * @property {string} [city_size]
 * @property {string} [psychiatric_diagnoses]
 * @property {any} [other]
 */
const DemographicsForm: React.FC<DemographicsFormProps> = ({ onSubmit, initialData }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<Record<string, unknown>>(initialData || defaultFormData);

  // Если e2eTestRunId появился в window после монтирования — обновить стейт
  useEffect(() => {
    const id = getE2eTestRunId();
    if (id && (formData as Record<string, string>).e2eTestRunId !== id) {
      setFormData(prev => ({ ...prev, e2eTestRunId: id }));
    }
  }, [formData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const buildDemographics = () => {
    // Централизовано: всегда берём e2eTestRunId из window, если есть
    const e2eId = getE2eTestRunId() || (formData as Record<string, string>).e2eTestRunId;
    return { ...formData, e2eTestRunId: e2eId };
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const demographics = buildDemographics();
    try {
      validateData('demographics.schema.json', demographics);
    } catch (e: any) {
      alert('Ошибка валидации демографических данных: ' + e.message);
      console.error('Ошибка валидации демографических данных:', e.message, demographics);
      return;
    }
    onSubmit(demographics);
    navigate('/test');
  };

  const handleSkip = () => {
    const demographics = buildDemographics();
    try {
      validateData('demographics.schema.json', demographics);
    } catch (e: any) {
      alert('Ошибка валидации демографических данных: ' + e.message);
      console.error('Ошибка валидации демографических данных:', e.message, demographics);
      return;
    }
    onSubmit({ e2eTestRunId: demographics.e2eTestRunId });
    navigate('/test');
  };

  const inputClasses = "mt-1 block w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition";
  const labelClasses = "block text-sm font-medium text-gray-700";

  return (
    <div className="bg-white p-8 rounded-2xl shadow-lg w-full max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">Демографическая информация</h2>
      <p className="text-gray-600 mb-8 text-center">
        Заполнение этой формы не является обязательным. Вы можете пропустить этот шаг.
      </p>
      <form onSubmit={handleSubmit} data-testid="demographics-form" className="space-y-6">
        <div>
          <label htmlFor="age" className={labelClasses}>Возраст</label>
          <input
            type="number"
            name="age"
            id="age"
            value={formData.age ?? ''}
            onChange={handleChange}
            className={inputClasses}
            placeholder="Введите ваш возраст"
            data-testid="age-input"
          />
        </div>
        <div>
          <label htmlFor="gender" className={labelClasses}>Пол</label>
          <select
            name="gender"
            id="gender"
            value={formData.gender ?? ''}
            onChange={handleChange}
            className={inputClasses}
            data-testid="gender-select"
          >
            <option value="">Выберите пол</option>
            <option value="male">Мужской</option>
            <option value="female">Женский</option>
            <option value="other">Другой</option>
          </select>
        </div>
        <div>
          <label htmlFor="psychiatric_diagnoses" className={labelClasses}>
            Укажите все, что посчитали релевантным данному тесту: психиатрические диагнозы, особенности поведения (включая экстремальные виды спорта, селфхарм), употребление ПАВ
          </label>
          <textarea
            name="psychiatric_diagnoses"
            id="psychiatric_diagnoses"
            value={formData.psychiatric_diagnoses ?? ''}
            onChange={handleChange}
            className={inputClasses}
            rows="3"
            placeholder="Если да, укажите какие"
            data-testid="diagnoses-input"
          ></textarea>
        </div>
        <input type="hidden" name="e2eTestRunId" value={formData.e2eTestRunId ?? ''} />
        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <button
            type="submit"
            data-testid="submit-demographics-button"
            className="w-full bg-indigo-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-transform transform hover:scale-105"
          >
            Продолжить
          </button>
          <button
            type="button"
            onClick={handleSkip}
            data-testid="skip-demographics-button"
            className="w-full bg-gray-200 text-gray-700 font-bold py-3 px-6 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-transform transform hover:scale-105"
          >
            Пропустить
          </button>
        </div>
      </form>
    </div>
  );
};

export default DemographicsForm;